import './styles/main.css';
import { usePlayerLayout } from 'lib/playerLayout';
import translationEN from './locales/en/translation.json';
import translationDE from './locales/de/translation.json';
import translationSR from './locales/sr/translation.json';
import translationHR from './locales/hr/translation.json';
import translationBS from './locales/bs/translation.json';
import translationIT from './locales/it/translation.json';
import translationFR from './locales/fr/translation.json';
import translationSQ from './locales/sq/translation.json';
import translationTR from './locales/tr/translation.json';
import translationES from './locales/es/translation.json';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Route, BrowserRouter } from 'react-router-dom';
import { ModernSlipSheet } from 'components/mobile/ModernSlipSheet/ModernSlipSheet';
import { CasinoGames } from 'pages/CasinoGames';
import { LiveCasino } from 'pages/LiveCasino';
import { CasinoReturn } from 'components/CasinoReturn/CasinoReturn';
import { LuckySix } from 'pages/LuckySix';
import { Keno } from 'pages/Keno';
import { WorkInProgress } from 'pages/WorkInProgress';
import UserContext from 'context/userContext';
import { NotFound } from 'pages/error-pages/NotFound';
import { Home } from 'pages/Home';
import { MobileLogin } from 'pages/MobileLogin';
import { LanguageSelection } from 'pages/LanguageSelection';
import {
  TOAST_AUTO_CLOSE_TIME,
  TOAST_ERROR_STYLE,
  WORK_IN_PROGRESS_PAGES,
} from 'config/constants';
import { ChangePassword } from 'pages/ChangePassword';
import { ThemeSettings } from 'pages/ThemeSettings';
// Self-exclusion (pages/AccountLock) and betting limits (pages/BettingLimits)
// are off the app for now (owner, 2026-10-29/30); the API still has them,
// the pages come back with their routes.
import {
  AccountSettings,
  ChangeEmail,
  MyBonuses,
  PersonalData,
} from 'pages/AccountPages';
import { ToastContainer } from 'react-toastify';
import { InstallApp } from 'components/InstallApp/InstallApp';
import { SportsBetting } from 'pages/SportsBetting';
import { LeaguesList } from 'pages/LeaguesList';
import { MatchOfferList } from 'pages/MatchOfferList';
import { OtherBetsList } from 'pages/OtherBetsList';
import SlipContext from 'context/slipContext';
import LeagueContext from 'context/leagueContext';
import { BetSlip } from 'pages/BetSlip';
import { MyBets } from 'pages/MyBets';
import { LiveBetting } from 'pages/LiveBetting';
import LiveBettingContext from 'context/liveBettingContext';
import PrematchCatalogProvider from 'context/prematchCatalogContext';
import 'setup/axiosSetup';
import 'setup/authHeaderSetup';
import 'setup/modalSetup';
import { HighLightsAndLastMinute } from 'pages/HighLightsAndLastMinute';
import { ErrorBoundaryWrapper } from 'error-boundary/ErrorBoundaryWrapper';
import { Search } from 'pages/Search';
import { Other } from 'pages/Other';
import { Contact } from 'pages/Contact';
import { AccountStatement } from 'pages/AccountStatement';
import { GamingHistory } from 'pages/GamingHistory';
import { Deposit } from 'pages/Deposit';
import { Results } from 'pages/Results';
import { ResultsLeaguesList } from 'pages/ResultsLeaguesList';
import { ResultsMatchOfferList } from 'pages/ResultsMatchOfferList';
import { AuthGate } from 'components/auth/AuthGate';
import PropTypes from 'prop-types';

export const CURRENCY = process.env.REACT_APP_CURRENCY ?? 'EUR';

const resources = {
  en: {
    translation: translationEN,
  },
  de: {
    translation: translationDE,
  },
  sr: {
    translation: translationSR,
  },
  hr: {
    translation: translationHR,
  },
  bs: {
    translation: translationBS,
  },
  it: {
    translation: translationIT,
  },
  fr: {
    translation: translationFR,
  },
  sq: {
    translation: translationSQ,
  },
  tr: {
    translation: translationTR,
  },
  es: {
    translation: translationES,
  },
};

const lang = localStorage.getItem('lang') ?? 'en';

i18n.use(initReactI18next).init({
  resources,
  lng: lang,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

const PAGE_COMPONENTS = [
  ['/', <Home />],
  ['/casino-games', <CasinoGames />],
  ['/live-casino', <LiveCasino />],
  ['/lucky-six', <LuckySix />],
  ['/keno', <Keno />],
  ['/login', <MobileLogin />],
  ['/language', <LanguageSelection />],
  ['/change-password', <ChangePassword />],
  ['/account-settings', <AccountSettings />],
  ['/theme-settings', <ThemeSettings />],
  ['/personal-data', <PersonalData />],
  ['/change-email', <ChangeEmail />],
  ['/my-bonuses', <MyBonuses />],
  [
    '/results',
    <LeagueContext>
      <Results />
    </LeagueContext>,
  ],
  [
    '/results/sport/:sport-name/:sport-id',
    <LeagueContext>
      <ResultsLeaguesList />
    </LeagueContext>,
  ],
  [
    '/results/:sport/:id/:league-name/:league-id',
    <LeagueContext>
      <ResultsMatchOfferList />
    </LeagueContext>,
  ],
  [
    '/sports-betting',
    <LeagueContext>
      <SportsBetting />
    </LeagueContext>,
  ],
  ['/sports-betting/sport/:sport-name/:sport-id', <LeaguesList />],
  ['/sports-betting/:sport/:id/:league-name/:league-id', <MatchOfferList />],
  ['/sports-betting/highlights', <HighLightsAndLastMinute />],
  ['/sports-betting/last-minute-events', <HighLightsAndLastMinute />],
  ['/sports-betting/most-played-events', <HighLightsAndLastMinute />],
  ['/sports-betting/match/:id', <OtherBetsList />],
  ['/live-betting', <LiveBetting />],
  ['/bet-slip', <BetSlip />],
  ['/my-bets', <MyBets />],
  ['/deposit', <Deposit />],
  ['/account-statement', <AccountStatement />],
  ['/gaming-history', <GamingHistory />],
  ['/search', <Search />],
  ['/info', <Other />],
  ['/contact', <Contact />],
];

function AppProviders({ children }) {
  return (
    <ErrorBoundaryWrapper>
      <UserContext>
        <PrematchCatalogProvider>
          <LiveBettingContext>
            <SlipContext>{children}</SlipContext>
          </LiveBettingContext>
        </PrematchCatalogProvider>
      </UserContext>
    </ErrorBoundaryWrapper>
  );
}

AppProviders.propTypes = {
  children: PropTypes.node,
};

function App() {
  // The modern layout paints its toasts in CSS (themes/modern.css): a dark
  // card over the tab bar instead of the pink banner on top.
  const modern = usePlayerLayout() === 'modern';
  return (
    <>
      <ToastContainer
        limit={1}
        pauseOnHover={false}
        autoClose={TOAST_AUTO_CLOSE_TIME}
        hideProgressBar={true}
        position={modern ? 'bottom-center' : 'top-right'}
        toastStyle={modern ? undefined : TOAST_ERROR_STYLE}
      />
      <BrowserRouter>
        <AppProviders>
          <AuthGate
            routes={PAGE_COMPONENTS.map(([route, element]) => (
              <Route key={route} path={route} element={element} />
            ))}
            workInProgressRoutes={WORK_IN_PROGRESS_PAGES.map((route) => (
              <Route key={route} path={route} element={<WorkInProgress />} />
            ))}
            notFound={<Route path="*" element={<NotFound />} />}
          />
          <CasinoReturn />
          <ModernSlipSheet />
          <InstallApp />
        </AppProviders>
      </BrowserRouter>
    </>
  );
}

export default App;
